import { useState, useEffect, useRef, useCallback } from 'react';
import { useApi } from '@/hooks/useApi';
import { useAuth } from '@/contexts/AuthContext';

interface NotificationItem {
  id: number;
  type: string;
  title: string;
  message: string;
  data: string | null;
  is_read: number;
  read_at: string | null;
  created_at: string;
}

export default function NotificationBell() {
  const { get, put } = useApi();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  const fetchUnread = useCallback(async () => {
    try {
      const data = await get<{ data: { count: number } }>('/notifications/unread-count');
      setUnreadCount(data.data?.count ?? 0);
    } catch { /* silent */ }
  }, [get]);

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const data = await get<{ data: NotificationItem[] }>('/notifications?limit=20');
      setNotifications(data.data || []);
    } catch { /* silent */ }
    setLoading(false);
  }, [get]);

  useEffect(() => {
    if (!user) return;
    fetchUnread();
    const interval = setInterval(fetchUnread, 30000);
    return () => clearInterval(interval);
  }, [user, fetchUnread]);

  useEffect(() => {
    if (open) {
      fetchNotifications();
      fetchUnread();
    }
  }, [open, fetchNotifications, fetchUnread]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const markRead = async (id: number) => {
    try {
      await put(`/notifications/${id}/read`, {});
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, is_read: 1, read_at: new Date().toISOString() } : n))
      );
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch { /* silent */ }
  };

  const markAllRead = async () => {
    try {
      await put('/notifications/mark-all-read', {});
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: 1, read_at: new Date().toISOString() })));
      setUnreadCount(0);
    } catch { /* silent */ }
  };

  const typeIcon: Record<string, string> = {
    document_review: 'ri-file-list-3-line',
    kyc_submitted: 'ri-file-add-line',
    kyc_reviewed: 'ri-check-double-line',
    default: 'ri-notification-3-line',
  };

  return (
    <div className="relative" ref={panelRef}>
      <button
        onClick={() => setOpen(!open)}
        className="relative w-9 h-9 flex items-center justify-center rounded-lg hover:bg-stone-100 text-stone-600 transition-colors cursor-pointer"
        aria-label="Notifications"
      >
        <i className="ri-notification-3-line text-lg" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-[360px] bg-white rounded-xl border border-stone-200 shadow-lg z-50 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-stone-100">
            <h3 className="font-semibold text-stone-900 text-sm">Notifications</h3>
            {unreadCount > 0 && (
              <button
                onClick={markAllRead}
                className="text-brand-600 text-xs font-medium hover:text-brand-700 cursor-pointer"
              >
                Mark all read
              </button>
            )}
          </div>

          <div className="max-h-[400px] overflow-y-auto">
            {loading && notifications.length === 0 && (
              <div className="p-6 text-center">
                <i className="ri-loader-4-line text-brand-600 animate-spin text-lg" />
              </div>
            )}

            {notifications.length === 0 && !loading && (
              <div className="p-6 text-center">
                <i className="ri-notification-off-line text-stone-300 text-2xl mb-2" />
                <p className="text-stone-400 text-sm">No notifications yet.</p>
              </div>
            )}

            {notifications.map((n) => (
              <div
                key={n.id}
                onClick={() => markRead(n.id)}
                className={`flex gap-3 px-4 py-3 cursor-pointer transition-colors border-b border-stone-50 last:border-0 ${
                  n.is_read ? 'bg-white' : 'bg-brand-50/50 hover:bg-brand-50'
                }`}
              >
                <div className="w-8 h-8 rounded-full bg-stone-100 flex items-center justify-center shrink-0 mt-0.5">
                  <i className={`${typeIcon[n.type] || typeIcon.default} text-stone-500 text-sm`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-stone-900">{n.title}</p>
                  <p className="text-xs text-stone-500 mt-0.5 line-clamp-2">{n.message}</p>
                  <p className="text-[11px] text-stone-400 mt-1">
                    {new Date(n.created_at).toLocaleString()}
                  </p>
                </div>
                {!n.is_read && <div className="w-2 h-2 rounded-full bg-brand-500 mt-2 shrink-0" />}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
